import LayerTree from '@gui/components/panels/data/LayerTree';
import SidePanelDialog from '@gui/components/panels/settings/SidePaneDialog';
import { ControllerManager } from '@gui/controller/ControllerManager';
import { Box, Button, Checkbox, Typography } from '@mui/material';
import { LayerKind } from '@visualizer/types';
import React, { useEffect, useState } from 'react';
import { getGrid } from 'store/LoadingStore';
import { addMixedLayerToCollection } from './DesignSchemeViewUtils';

export interface DesignSchemeControllerProps {
    setId: string;
    footprintId: string;
    schemeId: string;
    connectedLayerLabel?: string;
    showLinkCheckbox?: boolean;
    linkChecked?: boolean;
    onLinkChange?: (checked: boolean) => void;
}

/**
 * Generic controller that lets you select a Layer and associate it with a DesignScheme.
 * Intended to be embedded in scheme-specific controllers like FloorBasedController.
 */
export const DesignSchemeController: React.FC<DesignSchemeControllerProps> = ({
    setId,
    footprintId,
    schemeId,
    connectedLayerLabel = 'Linked Layer',
    showLinkCheckbox = false,
    linkChecked = true,
    onLinkChange,
}) => {
    // Controllers:
    const designSchemesController = ControllerManager.getInstance().getDesignSchemeController();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedLayerName, setSelectedLayerName] = useState<string>('Select Layer');
    const [layerTreeRefresh, setLayerTreeRefresh] = useState(0);

    // Load initial layer name:
    useEffect(() => {
        const ds = designSchemesController.getDesignScheme(schemeId);
        if (ds) {
            const layer = ds.getLayer(LayerKind.MAIN);
            if (layer && layer.name) {
                setSelectedLayerName(layer.name);
            }
        }
    }, [schemeId]);

    // Layer tree opening:
    const handleOpenLayerTree = () => {
        addMixedLayerToCollection(); // Make sure Mixer is in the layerTree.
        setLayerTreeRefresh((k) => k + 1); // Force refresh LayerTree to reflect changes in layer collection.
        setDialogOpen(true);
    };

    const handleCloseLayerTree = () => setDialogOpen(false);

    const handleLayerClick = (layerId: string) => {
        const grid = getGrid();
        if (!grid) return;

        const newLayer = grid.searchLayerByUID(layerId);
        if (!newLayer) return;

        // Get DesignScheme and update its layer:
        const ds = designSchemesController.getDesignScheme(schemeId);
        if (ds) {
            designSchemesController.updateSchemeLayer(setId!, footprintId, schemeId, LayerKind.MAIN, newLayer.getUID());
        }

        setSelectedLayerName(newLayer.name);
        handleCloseLayerTree();
    };

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                }}
            >
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap', flex: '0 0 72px' }}>
                    {connectedLayerLabel}
                </Typography>

                <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                    <Button variant="outlined" onClick={handleOpenLayerTree} fullWidth>
                        {selectedLayerName}
                    </Button>
                </Box>

                {/* Link Checkbox (Links the current DS to the selected layer) */}
                {showLinkCheckbox && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        <Checkbox
                            size="small"
                            checked={linkChecked}
                            onChange={(e) => onLinkChange?.(e.target.checked)}
                            data-testid="ds-link-checkbox"
                            sx={{ padding: 0, '& .MuiSvgIcon-root': { fontSize: '14px' } }}
                        />
                        <Typography sx={{ fontSize: '10px', whiteSpace: 'nowrap' }}>Link</Typography>
                    </Box>
                )}
            </Box>

            <SidePanelDialog
                open={dialogOpen}
                onClose={handleCloseLayerTree}
                side="right"
                title="Linked Layer"
                contentProps={{ sx: { p: 0 } }}
            >
                <Box sx={{ overflow: 'auto' }}>
                    <LayerTree key={layerTreeRefresh} onClick={handleLayerClick} />
                </Box>
            </SidePanelDialog>
        </Box>
    );
};

export interface GroupLabelProps {
    label: string;
    mt?: number;
    mb?: number;
}

export const GroupLabel: React.FC<GroupLabelProps> = ({ label, mt = 0.5, mb = 0.5 }) => {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mt,
                mb,
            }}
        >
            <Box
                sx={{
                    flexGrow: 1,
                    height: '1px',
                    bgcolor: 'divider',
                    opacity: 0.3,
                }}
            />

            <Typography
                sx={{
                    fontSize: '8px',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    whiteSpace: 'nowrap',
                }}
            >
                {label}
            </Typography>

            <Box
                sx={{
                    flexGrow: 1,
                    height: '1px',
                    bgcolor: 'divider',
                    opacity: 0.3,
                }}
            />
        </Box>
    );
};
